import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Trans, useTranslation } from 'react-i18next';
import { Form, Action, LineBox, ColouredLine } from './styles';
import { SignupValidation } from './validations';
import { useGoogleAuth, useSignup } from '../../../../apis/user/use';
import { generatePath } from 'react-router-dom';
import { ROUTE_LEGAL_PRIVACY, ROUTE_LEGAL_TOS } from '../../../../routes';
import {
  ErrorMessage,
  ZigAlertMessage,
  ZigButton,
  ZigInput,
  ZigTypography,
  ZigLink,
} from '@zignaly-open/ui';
import {
  Box,
  InputAdornment,
  Link,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { LoginPayload, SignupUserType } from '../../../../apis/user/types';
import Cookies from 'js-cookie';
import Mailcheck from 'react-mailcheck';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PasswordOutlinedIcon from '@mui/icons-material/PasswordOutlined';
import LockIcon from '@mui/icons-material/Lock';
import { isGoogleLoginEnabled, whitelabel } from '../../../../whitelabel';
import { SxProps } from '@mui/system';
import { gtagEvent } from '../../../../util/analytics';
import { GoogleButton, OrSeparator } from '../LoginForm/atoms';

const SignupForm: React.FC<{
  plain?: boolean;
  userType?: SignupUserType;
  onClickLogin: () => void;
  withTitle?: boolean;
  wrapperSx?: SxProps;
}> = ({
  plain,
  onClickLogin,
  userType,
  withTitle = true,
  wrapperSx = { py: 0, px: { xs: 2, sm: 4, md: 4, lg: 4 } },
}) => {
  const { t } = useTranslation(['auth', 'error']);
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<LoginPayload>({
    mode: 'all',
    resolver: yupResolver(SignupValidation),
    defaultValues: {
      email: '',
      password: '',
    },
  });
  const theme = useTheme();
  const { isLoading: loggingInWithGoogle, login: googleLogin } =
    useGoogleAuth();
  const [{ loading: signingUp }, signup] = useSignup();
  const [email, setEmail] = useState('');

  const onSubmit = (payload: LoginPayload) => {
    gtagEvent('cta_clicks', { event: 'form_submit', email: payload.email });
    signup({
      ...payload,
      userType: userType || 'investor',
      ref: Cookies.get('ref'),
      subtrack: Cookies.get('subtrack'),
    });
  };

  const xs = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      {!plain && (
        <LineBox>
          <ColouredLine />
          <Box flex={1} height={'100%'} />
        </LineBox>
      )}

      <Box sx={wrapperSx}>
        {withTitle && (
          <ZigTypography variant={'h1'} align={'center'} id={'signup__title'}>
            {t('signup-title')}
          </ZigTypography>
        )}
        {!plain && !xs && (
          <ZigTypography
            variant={'h2'}
            align={'center'}
            id={'signup__description'}
          >
            <Trans i18nKey={'signup-description'} t={t}>
              <Link
                underline={'always'}
                sx={{
                  color: 'neutral000',
                  textUnderlineOffset: '10px',
                  textDecorationColor: theme.palette.neutral000,
                }}
              />
            </Trans>
          </ZigTypography>
        )}
        <Form onSubmit={handleSubmit(onSubmit)}>
          {/* @ts-ignore */}
          <Mailcheck email={email}>
            {(suggested: { full: string }) => (
              <Controller
                name='email'
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <ZigInput
                    id={'signup__input-email'}
                    label={t('login-form.inputText.email.label') + ':'}
                    placeholder={t('login-form.inputText.email.label')}
                    disabled={signingUp}
                    error={t(errors.email?.message)}
                    helperText={
                      suggested && !email.endsWith('@tractionboard.io') ? (
                        <ErrorMessage
                          text={t('error:error.did-you-mean', {
                            suggested: suggested.full,
                          })}
                        />
                      ) : null
                    }
                    {...field}
                    onBlur={(e) => {
                      field.onBlur();
                      setEmail(e.target.value);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <EmailOutlinedIcon color={'secondary'} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            )}
          </Mailcheck>

          <Controller
            name='password'
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <ZigInput
                id={'signup__input-password'}
                sensitive
                label={t('login-form.inputText.password.label') + ':'}
                placeholder={t('login-form.inputText.password.label')}
                disabled={signingUp}
                error={t(errors.password?.message)}
                type={'password'}
                helperText={
                  <ZigAlertMessage
                    id={'signup__input-password-requirements'}
                    text={t('error:error.password-requirements', {
                      length: 8,
                    })}
                  />
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <PasswordOutlinedIcon color={'secondary'} />
                    </InputAdornment>
                  ),
                }}
                {...field}
              />
            )}
          />
          <ZigTypography
            marginTop={3}
            variant='h4'
            color='neutral300'
            component='h4'
            textAlign={'center'}
            id={'signup__accept-terms-and-conditions-label'}
          >
            <Trans i18nKey='signup-form.accept-terms' t={t}>
              <ZigLink
                href={whitelabel.links?.tos || generatePath(ROUTE_LEGAL_TOS)}
                target='_blank'
                rel='noopener'
              />
              <ZigLink
                href={
                  whitelabel.links?.privacyPolicy ||
                  generatePath(ROUTE_LEGAL_PRIVACY)
                }
                target='_blank'
                rel='noopener'
              />
            </Trans>
          </ZigTypography>

          <Action>
            <ZigButton
              type={'submit'}
              variant={'contained'}
              id={'signup__submit'}
              size={'xlarge'}
              loading={signingUp}
              disabled={loggingInWithGoogle}
              fullWidth={!plain}
            >
              {t('signup-form.submit')}
            </ZigButton>

            {isGoogleLoginEnabled && (
              <>
                <OrSeparator />
                <GoogleButton
                  disabled={signingUp}
                  loading={loggingInWithGoogle}
                  id={'signup__google'}
                  onClick={() => googleLogin()}
                >
                  {t('signup-form.google')}
                </GoogleButton>
              </>
            )}
          </Action>

          {!plain && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '5px',
                px: { xs: 2 },
              }}
            >
              <LockIcon color={'secondary'} fontSize={'small'} />
              <ZigTypography
                variant={'h3'}
                color={'neutral300'}
                textAlign={'center'}
                marginTop={'5px'}
                id={'signup__protect-label'}
              >
                {t('signup-protect')}
              </ZigTypography>
            </Box>
          )}
          {onClickLogin && (
            <ZigButton
              variant={'text'}
              id={'signup__login'}
              onClick={onClickLogin}
            >
              {t('signup-form.link.login')}
            </ZigButton>
          )}
        </Form>
      </Box>
    </>
  );
};

export default SignupForm;
